// src/pages/Jobs.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jobsService, driversService, companiesService } from "../../services";
import JobKanban from "./JobKanban";
import "./Jobs.css";
import "./JobKanban.css";

// Status options - Full workflow: Pending → Approved → Assigned → Picked Up → In Transit → Delivered
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'Picked Up', label: 'Picked Up' },
  { value: 'In Transit', label: 'In Transit' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Exception', label: 'Exception' },
  { value: 'Returned', label: 'Returned' },
  { value: 'Cancelled', label: 'Cancelled' },
];

// Date range options
const DATE_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Priority order', label: 'Urgent' },
];

// Status badge helper
const getStatusClass = (status) => {
  const statusMap = {
    'Pending': 'jobs-status-pending',
    'Approved': 'jobs-status-approved',
    'Assigned': 'jobs-status-assigned',
    'Picked Up': 'jobs-status-pickedup',
    'In Transit': 'jobs-status-transit',
    'Delivered': 'jobs-status-delivered',
    'Exception': 'jobs-status-exception',
    'Returned': 'jobs-status-returned',
    'Cancelled': 'jobs-status-cancelled',
  };
  return statusMap[status] || 'jobs-status-pending';
};

// Priority badge helper
const getPriorityClass = (priority) => {
  const priorityMap = {
    'Priority order': 'jobs-priority-urgent',
    'High': 'jobs-priority-high',
    'Medium': 'jobs-priority-normal',
    'Low': 'jobs-priority-low',
  };
  return priorityMap[priority] || 'jobs-priority-normal';
};

export default function Jobs() {
  const navigate = useNavigate();

  // View state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // For kanban (all statuses)
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  // Companies
  const [companies, setCompanies] = useState([]);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    assigned: 0,
    pickedUp: 0,
    inTransit: 0,
    delivered: 0,
    exception: 0,
    cancelled: 0,
  });

  // Assign modal states
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  // Create job modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [createForm, setCreateForm] = useState({
    fromName: '',
    fromAddress: '',
    toName: '',
    toAddress: '',
    priority: 'Medium',
    notes: '',
    scheduledDate: new Date().toISOString().split('T')[0],
  });

  // Time window states for assignment
  const [pickupDate, setPickupDate] = useState('');
  const [pickupWindowStart, setPickupWindowStart] = useState('');
  const [pickupWindowEnd, setPickupWindowEnd] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryWindowStart, setDeliveryWindowStart] = useState('');
  const [deliveryWindowEnd, setDeliveryWindowEnd] = useState('');

  // Menu state
  const [activeMenu, setActiveMenu] = useState(null);

  // Calculate date range based on filter
  const getDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
      case 'today':
        return { from: today.toISOString() };
      case '7days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return { from: sevenDaysAgo.toISOString() };
      case '30days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return { from: thirtyDaysAgo.toISOString() };
      case 'custom':
        return {
          from: customDateFrom ? new Date(customDateFrom).toISOString() : undefined,
          to: customDateTo ? new Date(customDateTo).toISOString() : undefined,
        };
      default:
        return {};
    }
  }, [dateFilter, customDateFrom, customDateTo]);

  // Fetch companies list (only companies with client portal users)
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await companiesService.getCompanies({ limit: 100, hasClients: true });
      setCompanies(response.companies || []);
    } catch (err) {
      console.error('Fetch companies error:', err);
    }
  }, []);

  // Calculate stats from jobs
  const calculateStats = (jobsList) => {
    const statsObj = {
      total: jobsList.length,
      pending: 0,
      approved: 0,
      assigned: 0,
      pickedUp: 0,
      inTransit: 0,
      delivered: 0,
      exception: 0,
      cancelled: 0,
    };

    jobsList.forEach(job => {
      switch (job.status) {
        case 'Pending': statsObj.pending++; break;
        case 'Approved': statsObj.approved++; break;
        case 'Assigned': statsObj.assigned++; break;
        case 'Picked Up': statsObj.pickedUp++; break;
        case 'In Transit': statsObj.inTransit++; break;
        case 'Delivered': statsObj.delivered++; break;
        case 'Exception': statsObj.exception++; break;
        case 'Cancelled': statsObj.cancelled++; break;
        default: break;
      }
    });

    return statsObj;
  };

  // Fetch jobs for list view
  const fetchJobs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange();
      const filters = {
        page,
        limit: 10,
        search: search || undefined,
        status: statusFilter || undefined,
        company: companyFilter || undefined,
        ...dateRange,
      };

      const response = await jobsService.getJobs(filters);

      setJobs(response.jobs || []);
      setPagination({
        page: response.pagination?.currentPage || page,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0,
      });

      // Also fetch stats (all jobs without pagination for accurate counts)
      const statsFilters = {
        limit: 100,
        company: companyFilter || undefined,
        ...dateRange,
      };
      const statsResponse = await jobsService.getJobs(statsFilters);
      setStats(calculateStats(statsResponse.jobs || []));
    } catch (err) {
      console.error('Fetch jobs error:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, companyFilter, getDateRange]);

  // Fetch all jobs for kanban view
  const fetchAllJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange();
      const filters = {
        limit: 100, // Backend max limit is 100
        search: search || undefined,
        company: companyFilter || undefined,
        ...dateRange,
      };

      const response = await jobsService.getJobs(filters);
      const jobsList = response.jobs || [];
      setAllJobs(jobsList);
      setStats(calculateStats(jobsList));
    } catch (err) {
      console.error('Fetch jobs error:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [search, companyFilter, getDateRange]);

  // Fetch companies on mount
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Fetch on mount and when filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (viewMode === 'list') {
        fetchJobs(1);
      } else {
        fetchAllJobs();
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [viewMode, fetchJobs, fetchAllJobs]);

  // Fetch drivers for assignment
  const fetchDrivers = async () => {
    try {
      const [available, all] = await Promise.all([
        driversService.getAvailableDrivers({ limit: 50 }),
        driversService.getDrivers({ limit: 100 }),
      ]);
      setAvailableDrivers(available.drivers || []);
      setAllDrivers(all.drivers || []);
    } catch (err) {
      console.error('Fetch drivers error:', err);
    }
  };

  // Filter drivers by search
  const filteredDrivers = (driverSearch ? allDrivers : availableDrivers).filter(driver => {
    if (!driverSearch) return true;
    const searchLower = driverSearch.toLowerCase();
    return (
      driver.firstName?.toLowerCase().includes(searchLower) ||
      driver.lastName?.toLowerCase().includes(searchLower) ||
      driver.email?.toLowerCase().includes(searchLower) ||
      driver.driverProfile?.vehicle?.toLowerCase().includes(searchLower)
    );
  });

  // Handle assign driver with time windows
  const handleAssignDriver = async (driverId) => {
    if (!selectedJob || !driverId) return;

    try {
      setAssigningDriver(true);

      const assignmentData = { driverId };

      if (pickupDate) {
        assignmentData.pickupDate = new Date(pickupDate).toISOString();
      }
      if (pickupWindowStart || pickupWindowEnd) {
        assignmentData.pickupWindow = {
          start: pickupWindowStart || null,
          end: pickupWindowEnd || null,
        };
      }
      if (deliveryDate) {
        assignmentData.deliveryDate = new Date(deliveryDate).toISOString();
      }
      if (deliveryWindowStart || deliveryWindowEnd) {
        assignmentData.deliveryWindow = {
          start: deliveryWindowStart || null,
          end: deliveryWindowEnd || null,
        };
      }

      await jobsService.assignDriver(selectedJob._id, assignmentData);

      setShowAssignModal(false);
      setSelectedJob(null);
      setSelectedDriverId(null);
      resetTimeWindowFields();

      if (viewMode === 'list') {
        fetchJobs(pagination.page);
      } else {
        fetchAllJobs();
      }
    } catch (err) {
      console.error('Assign driver error:', err);
      alert('Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  // Reset time window fields
  const resetTimeWindowFields = () => {
    setPickupDate('');
    setPickupWindowStart('');
    setPickupWindowEnd('');
    setDeliveryDate('');
    setDeliveryWindowStart('');
    setDeliveryWindowEnd('');
    setDriverSearch('');
  };

  // Handle update status
  const handleUpdateStatus = async (jobId, newStatus) => {
    try {
      await jobsService.updateStatus(jobId, newStatus);
      setActiveMenu(null);
      if (viewMode === 'list') {
        fetchJobs(pagination.page);
      } else {
        fetchAllJobs();
      }
    } catch (err) {
      console.error('Update status error:', err);
      alert(err.message || 'Failed to update status');
    }
  };

  // Handle approve job
  const handleApproveJob = async (jobId) => {
    try {
      await jobsService.approveJob(jobId);
      setActiveMenu(null);
      if (viewMode === 'list') {
        fetchJobs(pagination.page);
      } else {
        fetchAllJobs();
      }
    } catch (err) {
      console.error('Approve job error:', err);
      alert(err.message || 'Failed to approve job');
    }
  };

  // Handle cancel job
  const handleCancelJob = async (jobId) => {
    const reason = window.prompt('Enter cancellation reason (optional):');
    if (reason === null) return; // User clicked cancel

    try {
      await jobsService.cancelJob(jobId, reason);
      setActiveMenu(null);
      if (viewMode === 'list') {
        fetchJobs(pagination.page);
      } else {
        fetchAllJobs();
      }
    } catch (err) {
      console.error('Cancel job error:', err);
      alert(err.message || 'Failed to cancel job');
    }
  };

  // Check if job can be assigned (only Approved jobs can be assigned)
  const canAssignDriver = (job) => {
    return job.status === 'Approved' && !job.driver;
  };

  // Check if job can be cancelled (before Picked Up)
  const canCancelJob = (job) => {
    return ['Pending', 'Approved', 'Assigned'].includes(job.status);
  };

  // Handle delete job
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await jobsService.deleteJob(jobId);
      setActiveMenu(null);
      if (viewMode === 'list') {
        fetchJobs(pagination.page);
      } else {
        fetchAllJobs();
      }
    } catch (err) {
      console.error('Delete job error:', err);
      alert('Failed to delete job');
    }
  };

  // Handle create job
  const handleCreateJob = async () => {
    try {
      setCreatingJob(true);

      const jobData = {
        from: {
          name: createForm.fromName,
          address: createForm.fromAddress,
        },
        to: {
          name: createForm.toName,
          address: createForm.toAddress,
        },
        priority: createForm.priority,
        notes: createForm.notes,
        scheduledDate: new Date(createForm.scheduledDate).toISOString(),
      };

      await jobsService.createJob(jobData);

      setShowCreateModal(false);
      setCreateForm({
        fromName: '',
        fromAddress: '',
        toName: '',
        toAddress: '',
        priority: 'Medium',
        notes: '',
        scheduledDate: new Date().toISOString().split('T')[0],
      });

      if (viewMode === 'list') {
        fetchJobs(1);
      } else {
        fetchAllJobs();
      }
    } catch (err) {
      console.error('Create job error:', err);
      alert(err.message || 'Failed to create job');
    } finally {
      setCreatingJob(false);
    }
  };

  // Open assign modal
  const openAssignModal = (job) => {
    setSelectedJob(job);
    setShowAssignModal(true);
    setActiveMenu(null);
    setSelectedDriverId(null);
    setDriverSearch('');
    fetchDrivers();

    // Pre-fill with existing schedule if any
    if (job?.pickupDate) {
      setPickupDate(job.pickupDate.split('T')[0]);
    }
    if (job?.pickupWindow) {
      setPickupWindowStart(job.pickupWindow.start || '');
      setPickupWindowEnd(job.pickupWindow.end || '');
    }
    if (job?.deliveryDate) {
      setDeliveryDate(job.deliveryDate.split('T')[0]);
    }
    if (job?.deliveryWindow) {
      setDeliveryWindowStart(job.deliveryWindow.start || '');
      setDeliveryWindowEnd(job.deliveryWindow.end || '');
    }
  };

  // Navigate to job details
  const openJobDetails = (job) => {
    navigate(`/jobs/${job._id}`);
  };

  // Format job ID
  const formatJobId = (job) => {
    return job.jobId || `#${job._id?.slice(-4) || '0000'}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchJobs(newPage);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="jobs-page-container">
      {/* Header */}
      <div className="mq-welcome-block">
        <button className="mq-back-btn" onClick={() => navigate('/dashboard')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back Home
        </button>
        <h1 className="mq-welcome-name">Jobs</h1>
      </div>

      {/* Stats Cards */}
      <div className="jobs-stats-section">
        <div className="jobs-stat-card">
          <div className="jobs-stat-icon total">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="3" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 7H14M6 10H14M6 13H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="jobs-stat-info">
            <span className="jobs-stat-value">{stats.total}</span>
            <span className="jobs-stat-label">Total Jobs</span>
          </div>
        </div>
        <div className="jobs-stat-card">
          <div className="jobs-stat-icon pending">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 6V10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="jobs-stat-info">
            <span className="jobs-stat-value">{stats.pending}</span>
            <span className="jobs-stat-label">Pending</span>
          </div>
        </div>
        <div className="jobs-stat-card">
          <div className="jobs-stat-icon assigned">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M3 18C3 14.134 6.134 11 10 11C13.866 11 17 14.134 17 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="jobs-stat-info">
            <span className="jobs-stat-value">{stats.assigned}</span>
            <span className="jobs-stat-label">Assigned</span>
          </div>
        </div>
        <div className="jobs-stat-card">
          <div className="jobs-stat-icon transit">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M1 13H15V7L19 10V13H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="1" y="7" width="10" height="6" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="5" cy="15" r="2" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="15" cy="15" r="2" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          <div className="jobs-stat-info">
            <span className="jobs-stat-value">{stats.inTransit}</span>
            <span className="jobs-stat-label">In Transit</span>
          </div>
        </div>
        <div className="jobs-stat-card">
          <div className="jobs-stat-icon delivered">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 10L9 13L14 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="jobs-stat-info">
            <span className="jobs-stat-value">{stats.delivered}</span>
            <span className="jobs-stat-label">Delivered</span>
          </div>
        </div>
        <div className="jobs-stat-card">
          <div className="jobs-stat-icon exception">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L18 17H2L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M10 8V11M10 14V14.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="jobs-stat-info">
            <span className="jobs-stat-value">{stats.exception}</span>
            <span className="jobs-stat-label">Exception</span>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="jobs-error">
          <span>{error}</span>
          <button onClick={() => viewMode === 'list' ? fetchJobs(pagination.page) : fetchAllJobs()}>Retry</button>
        </div>
      )}

      <div className="jobs-card-wrapper">
        {/* Header Controls */}
        <div className="jobs-header-section">
          <div className="jobs-header-top">
            <div className="jobs-header-left">
              <h2 className="jobs-header-title">
                Jobs {pagination.total > 0 && <span className="jobs-count">({pagination.total})</span>}
              </h2>
              {/* View Toggle */}
              <div className="jobs-view-toggle">
                <button
                  className={`jobs-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4H14M2 8H14M2 12H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                <button
                  className={`jobs-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                  onClick={() => setViewMode('kanban')}
                  title="Kanban View"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="2" width="4" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="6" y="2" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="11" y="2" width="4" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="jobs-header-controls">
              {/* Search */}
              <div className="jobs-search-box">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                  <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="jobs-search-input"
                  placeholder="Search by job ID, client, address..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="jobs-search-clear" onClick={() => setSearch('')}>
                    ×
                  </button>
                )}
              </div>

              {/* Status Filter (only for list view) */}
              {viewMode === 'list' && (
                <select
                  className="jobs-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}

              {/* Date Filter */}
              <select
                className="jobs-filter-select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                {DATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* Company Filter */}
              <select
                className="jobs-filter-select"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="">All Companies</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>{company.name}</option>
                ))}
              </select>

              {/* Custom Date Range */}
              {dateFilter === 'custom' && (
                <div className="jobs-date-range">
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="jobs-date-input"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="jobs-date-input"
                  />
                </div>
              )}

              <button className="jobs-btn-new" onClick={() => setShowCreateModal(true)}>
                + New Job
              </button>
            </div>
          </div>
        </div>

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {loading && jobs.length === 0 ? (
              <div className="jobs-loading">
                <div className="jobs-spinner"></div>
                <p>Loading jobs...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="jobs-empty">
                <p>No jobs found</p>
                <small>Try adjusting your filters or create a new job</small>
              </div>
            ) : (
              <div className="mq-table-scroll"><table className="jobs-data-table">
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Client / Company</th>
                    <th>From → To</th>
                    <th>Priority</th>
                    <th>Driver</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job._id} className="jobs-table-row">
                      <td className="jobs-col-id" onClick={() => openJobDetails(job)}>
                        <span className="jobs-job-id">{formatJobId(job)}</span>
                      </td>
                      <td className="jobs-col-client" onClick={() => openJobDetails(job)}>
                        <div className="jobs-client-info">
                          <span className="jobs-client-name">
                            {job.client?.firstName
                              ? `${job.client.firstName} ${job.client.lastName || ''}`
                              : 'N/A'
                            }
                          </span>
                          {job.company?.name && (
                            <span className="jobs-company-name">{job.company.name}</span>
                          )}
                        </div>
                      </td>
                      <td className="jobs-col-route" onClick={() => openJobDetails(job)}>
                        <div className="jobs-route-cell">
                          <div className="jobs-route-from">
                            <span className="jobs-route-dot pickup"></span>
                            <span>{job.from?.name || job.from?.address?.substring(0, 20) || 'N/A'}</span>
                          </div>
                          <div className="jobs-route-to">
                            <span className="jobs-route-dot delivery"></span>
                            <span>{job.to?.name || job.to?.address?.substring(0, 20) || 'N/A'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="jobs-col-priority">
                        <span className={`jobs-priority-badge ${getPriorityClass(job.priority)}`}>
                          {job.priority || 'Medium'}
                        </span>
                      </td>
                      <td className="jobs-col-driver">
                        {job.driver ? (
                          <div className="jobs-driver-cell">
                            <span className="jobs-driver-avatar">
                              {job.driver.firstName?.[0] || 'D'}
                            </span>
                            <span className="jobs-driver-name">
                              {job.driver.firstName} {job.driver.lastName?.[0]}.
                            </span>
                          </div>
                        ) : canAssignDriver(job) ? (
                          <button
                            className="jobs-assign-btn"
                            onClick={() => openAssignModal(job)}
                          >
                            + Assign
                          </button>
                        ) : (
                          <span className="jobs-no-driver">—</span>
                        )}
                      </td>
                      <td className="jobs-col-status">
                        <span className={`jobs-status-badge ${getStatusClass(job.status)}`}>
                          {job.status || 'Pending'}
                        </span>
                      </td>
                      <td className="jobs-col-date">{formatDate(job.createdAt)}</td>
                      <td className="jobs-col-menu">
                        <button
                          className="jobs-menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenu(activeMenu === job._id ? null : job._id);
                          }}
                        >
                          ⋮
                        </button>
                        {activeMenu === job._id && (
                          <div className="jobs-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => openJobDetails(job)}>View Details</button>

                            {/* Pending → Approve */}
                            {job.status === 'Pending' && (
                              <button onClick={() => handleApproveJob(job._id)} className="jobs-menu-approve">
                                ✓ Approve Job
                              </button>
                            )}

                            {/* Approved → Assign Driver */}
                            {canAssignDriver(job) && (
                              <button onClick={() => openAssignModal(job)}>
                                👤 Assign Driver
                              </button>
                            )}

                            {/* Assigned → Picked Up (usually driver does this) */}
                            {job.status === 'Assigned' && (
                              <button onClick={() => handleUpdateStatus(job._id, 'Picked Up')}>
                                📦 Mark Picked Up
                              </button>
                            )}

                            {/* Picked Up → In Transit */}
                            {job.status === 'Picked Up' && (
                              <button onClick={() => handleUpdateStatus(job._id, 'In Transit')}>
                                🚚 Mark In Transit
                              </button>
                            )}

                            {/* In Transit → Delivered */}
                            {job.status === 'In Transit' && (
                              <button onClick={() => handleUpdateStatus(job._id, 'Delivered')}>
                                ✅ Mark Delivered
                              </button>
                            )}

                            {/* Exception → Returned or Retry */}
                            {job.status === 'Exception' && (
                              <>
                                <button onClick={() => handleUpdateStatus(job._id, 'In Transit')}>
                                  🔄 Retry Delivery
                                </button>
                                <button onClick={() => handleUpdateStatus(job._id, 'Returned')}>
                                  ↩️ Mark Returned
                                </button>
                              </>
                            )}

                            {/* Cancel option (before Picked Up) */}
                            {canCancelJob(job) && (
                              <button onClick={() => handleCancelJob(job._id)} className="jobs-menu-cancel">
                                ❌ Cancel Job
                              </button>
                            )}

                            <button
                              className="jobs-menu-delete"
                              onClick={() => handleDeleteJob(job._id)}
                            >
                              🗑️ Delete Job
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}

            {/* Pagination */}
            {jobs.length > 0 && (
              <div className="jobs-footer-section">
                <span className="jobs-footer-text">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="jobs-pagination-controls">
                  <button
                    className="jobs-pagination-btn"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Prev
                  </button>
                  {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        className={`jobs-pagination-btn ${pagination.page === pageNum ? 'jobs-pagination-active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    className="jobs-pagination-btn"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <JobKanban
            jobs={allJobs}
            onAssignDriver={openAssignModal}
            onUpdateStatus={handleUpdateStatus}
            loading={loading}
          />
        )}
      </div>

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="jobs-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="jobs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="jobs-modal-header">
              <h3>Create New Job</h3>
              <button className="jobs-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="jobs-modal-body">
              <div className="jobs-form">
                <div className="jobs-form-section">
                  <h4 className="jobs-form-title">Pickup Location</h4>
                  <div className="jobs-form-group">
                    <label>Name / Label</label>
                    <input
                      type="text"
                      value={createForm.fromName}
                      onChange={(e) => setCreateForm({ ...createForm, fromName: e.target.value })}
                      placeholder="e.g., Warehouse A"
                    />
                  </div>
                  <div className="jobs-form-group">
                    <label>Address *</label>
                    <input
                      type="text"
                      value={createForm.fromAddress}
                      onChange={(e) => setCreateForm({ ...createForm, fromAddress: e.target.value })}
                      placeholder="Enter full pickup address"
                    />
                  </div>
                </div>

                <div className="jobs-form-section">
                  <h4 className="jobs-form-title">Delivery Location</h4>
                  <div className="jobs-form-group">
                    <label>Name / Label</label>
                    <input
                      type="text"
                      value={createForm.toName}
                      onChange={(e) => setCreateForm({ ...createForm, toName: e.target.value })}
                      placeholder="e.g., Customer Office"
                    />
                  </div>
                  <div className="jobs-form-group">
                    <label>Address *</label>
                    <input
                      type="text"
                      value={createForm.toAddress}
                      onChange={(e) => setCreateForm({ ...createForm, toAddress: e.target.value })}
                      placeholder="Enter full delivery address"
                    />
                  </div>
                </div>

                <div className="jobs-form-row">
                  <div className="jobs-form-group">
                    <label>Priority</label>
                    <select
                      value={createForm.priority}
                      onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                    >
                      {PRIORITY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="jobs-form-group">
                    <label>Scheduled Date *</label>
                    <input
                      type="date"
                      value={createForm.scheduledDate}
                      onChange={(e) => setCreateForm({ ...createForm, scheduledDate: e.target.value })}
                    />
                  </div>
                </div>

                <div className="jobs-form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                    placeholder="Special instructions, package details, etc."
                    rows="3"
                  />
                </div>
              </div>
            </div>
            <div className="jobs-modal-footer">
              <button className="jobs-btn-outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="jobs-btn-primary"
                onClick={handleCreateJob}
                disabled={!createForm.fromAddress || !createForm.toAddress || !createForm.scheduledDate || creatingJob}
              >
                {creatingJob ? 'Creating...' : 'Create Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {showAssignModal && selectedJob && (
        <div className="jobs-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="jobs-modal jobs-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="jobs-modal-header">
              <h3>Assign Driver to {formatJobId(selectedJob)}</h3>
              <button className="jobs-modal-close" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="jobs-modal-body">
              {/* Driver Search */}
              <div className="jobs-driver-search">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                  <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search drivers by name, email, or vehicle..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                />
                {driverSearch && (
                  <button className="jobs-search-clear" onClick={() => setDriverSearch('')}>×</button>
                )}
              </div>

              {/* Driver Selection */}
              <div className="jobs-assign-section">
                <h4 className="jobs-section-title">
                  {driverSearch ? 'Search Results' : 'Available Drivers'}
                  <span className="jobs-driver-count">({filteredDrivers.length})</span>
                </h4>
                {filteredDrivers.length === 0 ? (
                  <div className="jobs-empty">
                    <p>{driverSearch ? 'No drivers match your search' : 'No available drivers'}</p>
                  </div>
                ) : (
                  <div className="jobs-drivers-list">
                    {filteredDrivers.map((driver) => {
                      const activeDeliveries = driver.driverProfile?.completedDeliveries !== undefined
                        ? (driver.driverProfile?.totalDeliveries || 0) - (driver.driverProfile?.completedDeliveries || 0)
                        : driver.activeJobs || 0;
                      return (
                        <div
                          key={driver._id}
                          className={`jobs-driver-item ${selectedDriverId === driver._id ? 'jobs-driver-selected' : ''}`}
                          onClick={() => setSelectedDriverId(driver._id)}
                        >
                          <div className="jobs-driver-info">
                            <div className="jobs-driver-avatar-lg">
                              {driver.firstName?.[0] || 'D'}
                            </div>
                            <div className="jobs-driver-details-wrap">
                              <div className="jobs-driver-name-lg">
                                {driver.firstName} {driver.lastName}
                              </div>
                              <div className="jobs-driver-meta">
                                {driver.driverProfile?.vehicle || 'No vehicle'} • ★ {driver.rating?.toFixed(1) || '0.0'}
                              </div>
                              <div className="jobs-driver-stats-row">
                                <span className={`jobs-driver-status ${driver.driverProfile?.status === 'Online' ? 'online' : 'offline'}`}>
                                  {driver.driverProfile?.status || 'Offline'}
                                </span>
                                <span className="jobs-driver-active-count">
                                  {activeDeliveries} active {activeDeliveries === 1 ? 'delivery' : 'deliveries'}
                                </span>
                              </div>
                            </div>
                          </div>
                          {selectedDriverId === driver._id && (
                            <span className="jobs-driver-check">✓</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Time Windows Section */}
              {selectedDriverId && (
                <div className="jobs-assign-section">
                  <h4 className="jobs-section-title">Schedule (Optional)</h4>

                  <div className="jobs-schedule-group">
                    <label className="jobs-schedule-label">Pickup Schedule</label>
                    <div className="jobs-schedule-row">
                      <div className="jobs-schedule-field">
                        <label>Date</label>
                        <input
                          type="date"
                          value={pickupDate}
                          onChange={(e) => setPickupDate(e.target.value)}
                          className="jobs-schedule-input"
                        />
                      </div>
                      <div className="jobs-schedule-field">
                        <label>From</label>
                        <input
                          type="time"
                          value={pickupWindowStart}
                          onChange={(e) => setPickupWindowStart(e.target.value)}
                          className="jobs-schedule-input"
                        />
                      </div>
                      <div className="jobs-schedule-field">
                        <label>To</label>
                        <input
                          type="time"
                          value={pickupWindowEnd}
                          onChange={(e) => setPickupWindowEnd(e.target.value)}
                          className="jobs-schedule-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="jobs-schedule-group">
                    <label className="jobs-schedule-label">Delivery Schedule</label>
                    <div className="jobs-schedule-row">
                      <div className="jobs-schedule-field">
                        <label>Date</label>
                        <input
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          className="jobs-schedule-input"
                        />
                      </div>
                      <div className="jobs-schedule-field">
                        <label>From</label>
                        <input
                          type="time"
                          value={deliveryWindowStart}
                          onChange={(e) => setDeliveryWindowStart(e.target.value)}
                          className="jobs-schedule-input"
                        />
                      </div>
                      <div className="jobs-schedule-field">
                        <label>To</label>
                        <input
                          type="time"
                          value={deliveryWindowEnd}
                          onChange={(e) => setDeliveryWindowEnd(e.target.value)}
                          className="jobs-schedule-input"
                        />
                      </div>
                    </div>
                  </div>

                  <p className="jobs-schedule-hint">
                    Driver can modify these time windows if needed
                  </p>
                </div>
              )}
            </div>
            <div className="jobs-modal-footer">
              <button className="jobs-btn-outline" onClick={() => {
                setShowAssignModal(false);
                setSelectedDriverId(null);
                resetTimeWindowFields();
              }}>
                Cancel
              </button>
              <button
                className="jobs-btn-primary"
                onClick={() => handleAssignDriver(selectedDriverId)}
                disabled={!selectedDriverId || assigningDriver}
              >
                {assigningDriver ? 'Assigning...' : 'Assign Driver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
